import { MESSAGE_TYPE } from "../api/enums";

const RICH_CONTENT_VERSION = 1;

function parseRichContent(content) {
    try {
        const data = typeof content === "string" ? JSON.parse(content) : content;
        if (!data || !Array.isArray(data.blocks)) {
            return null;
        }
        return data;
    } catch (e) {
        return null;
    }
}

function isRichTextMessage(msgInfo) {
    return !!msgInfo && msgInfo.type === MESSAGE_TYPE.RICH_TEXT;
}

function createRichContent(blocks) {
    return {
        version: RICH_CONTENT_VERSION,
        blocks
    };
}

function stripMediaMeasureFields(item) {
    if (!item) {
        return null;
    }
    const normalized = { ...item };
    delete normalized.width;
    delete normalized.height;
    delete normalized.aspectRatio;
    return normalized;
}

function normalizeRichMediaItem(item) {
    if (!item) {
        return null;
    }
    const normalized = { ...item };
    delete normalized.aspectRatio;
    if (normalized.width !== undefined && normalized.width !== null) {
        normalized.width = Number(normalized.width) || 0;
    }
    if (normalized.height !== undefined && normalized.height !== null) {
        normalized.height = Number(normalized.height) || 0;
    }
    if (!normalized.width || !normalized.height) {
        delete normalized.width;
        delete normalized.height;
    }
    return normalized;
}

function normalizeRichContent(content) {
    const data = parseRichContent(content);
    if (!data) {
        return content;
    }
    const blocks = data.blocks.map((block) => {
        if (block.type !== "media" || !Array.isArray(block.items)) {
            return block;
        }
        return {
            ...block,
            items: block.items.map(normalizeRichMediaItem)
        };
    });
    return JSON.stringify({
        version: data.version || RICH_CONTENT_VERSION,
        blocks
    });
}

function segmentsToPlainText(segments = []) {
    return segments.map((segment) => {
        if (segment.kind === "text") {
            return segment.text || "";
        }
        if (segment.kind === "emoji") {
            return segment.code || "";
        }
        if (segment.kind === "at") {
            return segment.text || "";
        }
        if (segment.kind === "break") {
            return "\n";
        }
        return "";
    }).join("");
}

function richTextPreview(data, fallback = "") {
    if (!data || !Array.isArray(data.blocks)) {
        return fallback;
    }
    let text = "";
    let mediaCount = 0;
    let hasVideo = false;
    data.blocks.forEach((block) => {
        if (block.type === "text" && Array.isArray(block.segments)) {
            text += segmentsToPlainText(block.segments);
        }
        if (block.type === "media" && Array.isArray(block.items)) {
            mediaCount += block.items.length;
            if (block.items.some((item) => item.kind === "video")) {
                hasVideo = true;
            }
        }
    });
    text = (text || "").trim();
    if (text) {
        return text;
    }
    if (mediaCount > 0) {
        if (hasVideo && mediaCount > 1) {
            return `[${mediaCount}${mediaCount > 1 ? "项" : ""}图文媒体]`;
        }
        if (hasVideo) {
            return "[视频图文]";
        }
        return `[${mediaCount}${mediaCount > 1 ? "张" : "张"}图片图文]`;
    }
    return fallback;
}

function extractRichMediaItems(data) {
    if (!data || !Array.isArray(data.blocks)) {
        return [];
    }
    const items = [];
    data.blocks.forEach((block) => {
        if (block.type === "media" && Array.isArray(block.items)) {
            block.items.forEach((item) => {
                if (item) {
                    items.push(item);
                }
            });
        }
    });
    return items;
}

function firstRichMediaItem(data) {
    const items = extractRichMediaItems(data);
    return items.length ? items[0] : null;
}

function deriveAtUserIds(blocks = []) {
    const idSet = new Set();
    blocks.forEach((block) => {
        if (block.type !== "text" || !Array.isArray(block.segments)) {
            return;
        }
        block.segments.forEach((segment) => {
            if (segment.kind === "at" && segment.userId !== undefined && segment.userId !== null) {
                idSet.add(Number(segment.userId));
            }
        });
    });
    return Array.from(idSet);
}

function trimRichTextBlocks(blocks = []) {
    return blocks.filter((block) => {
        if (block.type !== "text") {
            return true;
        }
        const text = segmentsToPlainText(block.segments || []).trim();
        return !!text;
    });
}

function buildSingleMediaContent(item) {
    const normalized = stripMediaMeasureFields(item);
    return JSON.stringify(normalized);
}

function isAnimatedImage(item) {
    if (!item) {
        return false;
    }
    const urls = [item.originUrl, item.thumbUrl, item.imageUrl].filter(Boolean);
    return urls.some((url) => /\.(gif|webp)(?:[?#].*)?$/i.test(url));
}

function resolveImageDisplayUrl(item) {
    if (!item) {
        return "";
    }
    if (isAnimatedImage(item)) {
        return item.originUrl || item.thumbUrl || item.imageUrl || "";
    }
    return item.thumbUrl || item.originUrl || item.imageUrl || "";
}

function getRichMediaKey(item) {
    if (!item) {
        return "";
    }
    return item.kind === "video" ? item.videoUrl || item.coverUrl || "" : item.originUrl || item.thumbUrl || "";
}

export {
    RICH_CONTENT_VERSION,
    parseRichContent,
    isRichTextMessage,
    createRichContent,
    normalizeRichContent,
    normalizeRichMediaItem,
    stripMediaMeasureFields,
    segmentsToPlainText,
    richTextPreview,
    extractRichMediaItems,
    firstRichMediaItem,
    deriveAtUserIds,
    trimRichTextBlocks,
    buildSingleMediaContent,
    isAnimatedImage,
    resolveImageDisplayUrl,
    getRichMediaKey
};
